import type { KeyboardEvent } from 'react';
import type { QuoteLayoutSection } from '../types';
import { cn } from '../ui/primitives';

/** One entry in the strip. Several sections can stand behind it. */
export interface QuoteTab {
	id: string;
	label: string;
	count?: number;
}

/** Which tab a section belongs to. Ungrouped sections are a tab of their own. */
export const tabIdOf = (section: QuoteLayoutSection) => section.tabGroup?.id ?? section.id;

/** The ids the tab and its panel point at each other with. One place decides,
 * so `aria-controls` and `aria-labelledby` can't disagree. */
export const tabPanelId = (tabId: string) => `quote-tabpanel-${tabId}`;
export const tabButtonId = (tabId: string) => `quote-tab-${tabId}`;

/**
 * Folds the section list into the tab strip, in first-appearance order.
 *
 * A grouped tab only carries a count when exactly one of its sections declares
 * one. Summing across a tab that covers two managers and one vetter would put a
 * "3" on it that counts nothing anybody asked about, and there is no honest
 * single number for a tab holding several different kinds of thing — so it gets
 * none.
 */
export function toTabs(sections: QuoteLayoutSection[]): QuoteTab[] {
	const tabs: QuoteTab[] = [];
	const seen = new Map<string, QuoteTab>();
	const counted = new Map<string, number>();
	for (const section of sections) {
		const id = tabIdOf(section);
		let tab = seen.get(id);
		if (!tab) {
			tab = { id, label: section.tabGroup?.label ?? section.label };
			seen.set(id, tab);
			tabs.push(tab);
		}
		if (section.count != null) {
			tab.count = section.count;
			counted.set(id, (counted.get(id) ?? 0) + 1);
		}
	}
	for (const tab of tabs) {
		if ((counted.get(tab.id) ?? 0) > 1) tab.count = undefined;
	}
	return tabs;
}

/**
 * The strip itself.
 *
 * Scrolls sideways rather than wrapping — a wrapped tab row reads as two rows of
 * unrelated things, and the page is wide enough that it rarely has to.
 *
 * It claims `tablist`, so it owes the behaviour that role promises: the arrow
 * keys move between tabs, Home and End jump to the ends, and only the open tab
 * is a tab stop, so Tab from the strip goes into the panel rather than through
 * three tabs the reader didn't ask for. Claiming the role without the keys would
 * leave a screen-reader user worse off than plain buttons would have.
 */
export function QuoteTabs({
	tabs,
	active,
	onSelect,
}: {
	tabs: QuoteTab[];
	active: string;
	onSelect: (id: string) => void;
}) {
	/* Selection follows focus, which is the right choice here: the panels are all
	 * rendered already, so arrowing through them costs nothing.
	 *
	 * Left and right only. The strip is horizontal, so up and down are still the
	 * reader's to scroll the page with — claiming them would mean calling
	 * preventDefault on the one key a long page needs most. */
	const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
		const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
		let next = -1;
		if (step !== 0) next = (index + step + tabs.length) % tabs.length;
		else if (event.key === 'Home') next = 0;
		else if (event.key === 'End') next = tabs.length - 1;
		if (next < 0) return;

		event.preventDefault();
		onSelect(tabs[next].id);
		/* `onSelect` may already be scrolling the strip into view; focus() would
		   otherwise jump there instantly and fight the smooth scroll. */
		document.getElementById(tabButtonId(tabs[next].id))?.focus({ preventScroll: true });
	};

	return (
		<div
			role="tablist"
			aria-label="Quote sections"
			className="flex gap-0 overflow-x-auto border-b border-line-1"
		>
			{tabs.map((tab, i) => {
				const on = active === tab.id;
				return (
					<button
						key={tab.id}
						id={tabButtonId(tab.id)}
						type="button"
						role="tab"
						aria-selected={on}
						aria-controls={tabPanelId(tab.id)}
						tabIndex={on ? 0 : -1}
						onClick={() => onSelect(tab.id)}
						onKeyDown={(event) => handleKeyDown(event, i)}
						className={cn(
							'relative shrink-0 cursor-pointer bg-transparent px-4 pt-1 pb-2.5 text-base font-medium transition-colors duration-[120ms]',
							i === 0 && 'pl-0',
							on ? 'text-text-1' : 'text-text-3 hover:text-text-2'
						)}
					>
						<span className="flex items-center gap-1.5">
							{tab.label}
							{tab.count != null && (
								<span
									className={cn(
										'rounded-full px-1.5 py-px font-mono text-2xs',
										on
											? 'bg-accent-soft text-accent'
											: 'bg-surface-fill text-text-4'
									)}
								>
									{tab.count}
								</span>
							)}
						</span>
						{on && (
							<span
								className={cn(
									'absolute right-4 bottom-0 h-[1.5px] rounded-full bg-accent',
									i === 0 ? 'left-0' : 'left-4'
								)}
							/>
						)}
					</button>
				);
			})}
		</div>
	);
}
